import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Trophy, Medal, Crown, TrendingUp, Zap } from 'lucide-react';
import { fetchLeaderboard } from '../api/gamification';
import { useAuthStore } from '../store/authStore';
import type { LeaderboardEntry } from '../types';

export const Leaderboard: React.FC = () => {
  const { user } = useAuthStore();
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLeaderboard()
      .then(setEntries)
      .catch(() => setEntries([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="flex items-center gap-3 text-text-dim">
          <div className="w-4 h-4 border-2 border-white/10 border-t-gold rounded-full animate-spin" />
          Loading leaderboard...
        </div>
      </div>
    );
  }

  const _userRank = entries.findIndex((e) => e.username === user?.username);
  void _userRank;

  return (
    <div className="p-4 md:p-8 pb-24 md:pb-8 max-w-3xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-5"
      >
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-14 h-14 rounded-full bg-gold-glow border border-gold-dim flex items-center justify-center mx-auto mb-3">
            <Trophy size={24} className="text-gold" />
          </div>
          <h2 className="font-display text-2xl text-cream mb-1">Weekly Leaderboard</h2>
          <p className="text-sm text-text">Top performers by accuracy this week</p>
        </div>

        {/* Top 3 podium */}
        {entries.length >= 3 && (
          <div className="flex items-end justify-center gap-3 mb-6">
            <PodiumItem entry={entries[1]} rank={2} color="text-text" bg="bg-surface-2" />
            <PodiumItem entry={entries[0]} rank={1} color="text-gold" bg="bg-gold-glow" border borderColor="border-gold-dim" tall />
            <PodiumItem entry={entries[2]} rank={3} color="text-amber" bg="bg-amber-muted" />
          </div>
        )}

        {/* List */}
        <div className="glass-card p-4 rounded-2xl">
          <div className="space-y-1">
            {entries.map((entry, i) => {
              const isMe = entry.username === user?.username;
              return (
                <motion.div
                  key={entry.username + i}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.03, duration: 0.25 }}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors ${
                    isMe ? 'bg-gold-glow border border-gold-dim' : 'hover:bg-surface-1'
                  }`}
                >
                  <div className="w-6 text-center">
                    {i === 0 ? (
                      <Crown size={16} className="text-gold mx-auto" />
                    ) : i === 1 ? (
                      <Medal size={16} className="text-text mx-auto" />
                    ) : i === 2 ? (
                      <Medal size={16} className="text-amber mx-auto" />
                    ) : (
                      <span className="text-xs text-text-dim font-mono">{i + 1}</span>
                    )}
                  </div>

                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gold/20 to-gold/5 border border-gold-dim flex items-center justify-center text-xs font-bold text-gold-text shrink-0">
                    {entry.username.charAt(0).toUpperCase()}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-medium truncate ${isMe ? 'text-gold-text' : 'text-text-hi'}`}>
                        {entry.username}
                      </span>
                      {isMe && (
                        <span className="text-[9px] font-semibold uppercase tracking-wider text-gold bg-gold-glow px-1.5 py-0.5 rounded-full border border-gold-dim">
                          You
                        </span>
                      )}
                    </div>
                    <span className="text-[10px] text-text-dim">Level {entry.level}</span>
                  </div>

                  <div className="flex items-center gap-4 shrink-0">
                    <div className="text-right">
                      <div className="flex items-center gap-1 text-xs text-text-hi">
                        <TrendingUp size={12} className="text-green" />
                        <span className="font-mono">{Math.round(entry.accuracy * 100)}%</span>
                      </div>
                      <p className="text-[10px] text-text-dim flex items-center justify-end gap-1">
                        <Zap size={10} className="text-gold" />
                        {entry.xp} XP
                      </p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {entries.length === 0 && (
            <div className="text-center py-10">
              <p className="text-sm text-text-dim">No leaderboard data yet. Be the first!</p>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

const PodiumItem: React.FC<{
  entry: LeaderboardEntry;
  rank: number;
  color: string;
  bg: string;
  border?: boolean;
  borderColor?: string;
  tall?: boolean;
}> = ({ entry, rank, color, bg, border, borderColor, tall }) => (
  <div className={`flex flex-col items-center ${tall ? 'pb-0' : 'pb-2'}`}>
    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gold/20 to-gold/5 border border-gold-dim flex items-center justify-center text-sm font-bold text-gold-text mb-2">
      {entry.username.charAt(0).toUpperCase()}
    </div>
    <p className={`text-xs font-medium truncate max-w-[80px] ${color}`}>{entry.username}</p>
    <p className="text-[10px] text-text-dim font-mono mb-1">{Math.round(entry.accuracy * 100)}%</p>
    <div
      className={`w-20 rounded-t-lg ${bg} ${border ? 'border-t border-x' : ''}`}
      style={{
        height: tall ? 80 : 48,
        borderColor: borderColor || undefined,
      }}
    >
      <div className="flex items-center justify-center h-full">
        <span className={`text-lg font-display font-bold ${color}`}>{rank}</span>
      </div>
    </div>
  </div>
);
